import { Fragment, useContext, useEffect, useState } from 'react';
import { useCustomerAPI } from '../../clients/customer-api/customer-api-client';
import { useCustomer } from '../../hooks/use-customer-context';
import { UserAgreement } from '../../clients/quipli-service/agreement-documents/agreement-document';
import { Checkbox } from '../../components/checkbox/checkbox';
import { Typography } from '../../components/typography/typography';
import { Configuration } from '../../clients/quipli-service/configuration/configuration';
import { EcommerceButton } from '../../components/form/button';
import { Label } from '../../clients/quipli-service/label/label';
import Icon from '../../components/icons/icon';
import { StepAction } from './checkout-view';
import { TenantContext } from '../../context/tenant-context';
import { DateTime } from 'luxon';
import { Spinner } from '../../components/spinner/spinner';
import { TextArea } from '../../components/textarea/textarea';

export const CheckoutLegal = ({
  currentStep,
  conf,
  labels,
  notes,
  setNotes,
  digitalSignature,
  setDigitalSignature,
  isSubmitting,
  onPlaceOrder,
  isQuote = false,
}: {
  currentStep: StepAction;
  conf: Configuration[];
  labels: Label[];
  notes: string;
  setNotes: (notes: string) => void;
  digitalSignature?: { signature: string; date: DateTime };
  setDigitalSignature: (signature: { signature: string; date: DateTime }) => void;
  isSubmitting: boolean;
  onPlaceOrder?: () => void;
  isQuote?: boolean;
}) => {
  const { listUserAgreements } = useCustomerAPI();
  const { contextPath } = useContext(TenantContext);
  const customer = useCustomer();

  const [agreedDocs, setAgreedDocs] = useState<UserAgreement[]>([]);
  const [pendingDocs, setPendingDocs] = useState<UserAgreement[]>([]);
  const [checkedDocs, setCheckedDocs] = useState<number[]>([]);

  const electronictConsent = labels.find((l) => l.key === 'M_ELECTRONIC_CONSENT')?.caption;
  const digitalSignatureEnabled = conf.find((c) => c.variable === 'CONF_DIGITAL_SIGNATURE')?.value === '1';
  const areNotesRequired = conf.find((c) => c.variable === 'CONF_REQUIRED_ORDER_NOTES')?.value === '1';
  const isDisabled =
    currentStep !== StepAction.Legal ||
    checkedDocs.length !== pendingDocs.length ||
    (digitalSignatureEnabled && !digitalSignature) ||
    (!isQuote && areNotesRequired && !notes);

  const agreedDocsLinks = () => {
    return agreedDocs.map((ua, idx) => (
      <Fragment key={`anchor-${idx}`}>
        {idx > 0 ? (idx === agreedDocs.length - 1 ? ' and ' : ', ') : ''}
        <a
          className="font-semibold text-primary-500 hover:text-primary-500"
          target="_blank"
          href={`${contextPath}cart/legaldocument/${ua.agreementDocument.customAgreementType.id}`}
        >
          {ua.agreementDocument.customAgreementType.name}
        </a>
      </Fragment>
    ));
  };

  const generateDigitalSignature = () => {
    if (!customer) return;

    setDigitalSignature({
      signature: customer.name,
      date: DateTime.now(),
    });
  };

  const handleSubmit = () => {
    if (isDisabled || isSubmitting) return;

    onPlaceOrder?.();
  };

  useEffect(() => {
    if (customer) {
      listUserAgreements().then((agreements) => {
        setAgreedDocs(agreements.filter((ua) => ua.userAgreement !== undefined));
        setPendingDocs(agreements.filter((ua) => ua.userAgreement === undefined));
      });
    }
  }, [customer]);

  return (
    <div className="flex flex-col gap-6 py-6">
      {!isQuote && (
        <TextArea
          label={labels?.find((l) => l.key === 'L_ORDER_NOTES_BLOCK_TITLE')?.caption || 'Order Notes'}
          labelType="text-lg"
          labelWeight="medium"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder={conf?.find((c) => c.variable === 'CONF_ORDER_NOTES_PLACEHOLDER')?.value}
          className="mt-2 text-sm"
          rows={3}
          autoResize
          disabled={isSubmitting}
          isRequired={areNotesRequired}
        />
      )}

      {customer && (
        <div className="flex flex-col gap-4">
          <div className="flex gap-2 flex-col">
            {pendingDocs.map(({ agreementDocument }) => (
              <div key={`agreement-${agreementDocument.customAgreementType.id}`} className="flex">
                <Checkbox
                  type="ecommercePrimary"
                  isChecked={checkedDocs.includes(agreementDocument.customAgreementType.id)}
                  onChange={(checked) => {
                    if (checked) {
                      setCheckedDocs([...checkedDocs, agreementDocument.customAgreementType.id]);
                    } else {
                      setCheckedDocs(checkedDocs.filter((id) => id !== agreementDocument.customAgreementType.id));
                    }
                  }}
                  isDisabled={isSubmitting}
                  dataCy="pending_docs_checkbox"
                />
                <Typography type="text-sm" className="flex gap-1">
                  {agreementDocument.customAgreementType.displayText}
                  <a
                    className="font-semibold text-primary-500 hover:text-primary-500"
                    target="_blank"
                    href={`${contextPath}cart/legaldocument/${agreementDocument.customAgreementType.id}`}
                  >
                    {agreementDocument.customAgreementType.name}
                  </a>
                </Typography>
              </div>
            ))}
          </div>

          {digitalSignatureEnabled &&
            (digitalSignature ? (
              <div className="flex justify-around">
                <div className="flex flex-col items-center">
                  <Typography type="text-sm" weight="bold">
                    Customer Signature
                  </Typography>
                  <span className="signature_text">{digitalSignature.signature}</span>
                </div>

                <div className="flex flex-col items-center">
                  <Typography type="text-sm" weight="bold">
                    Today's Date
                  </Typography>
                  <span className="signature_date">{digitalSignature.date.toFormat('D T ZZZZ')}</span>
                </div>
              </div>
            ) : (
              <div>
                <EcommerceButton dataCy="digital_signature" onClick={generateDigitalSignature}>
                  Generate my digital signature
                </EcommerceButton>
              </div>
            ))}

          {agreedDocs.length > 0 && (
            <Typography type="text-sm">
              {`By ${isQuote ? 'accepting' : 'placing'} this ${
                isQuote ? 'quote' : labels.find((l) => l.key === 'L_ORDER')?.caption || 'order'
              }, I agree to `}
              {agreedDocsLinks()}.
            </Typography>
          )}

          {isSubmitting ? (
            <Spinner />
          ) : (
            <EcommerceButton disabled={isDisabled} onClick={handleSubmit} dataCy="place_order_request">
              {isQuote
                ? 'Accept quote'
                : labels.find((l) => l.key === 'L_PLACE_ORDER_REQUEST')?.caption || 'Place order request'}
            </EcommerceButton>
          )}

          {electronictConsent && (
            <div className="flex gap-2">
              <Icon icon="InfoCircle" stroke="var(--grey-400)" />
              <Typography type="text-sm">{electronictConsent}</Typography>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
